'use client';

import { createContext, useContext, useState, useCallback } from 'react';
import { masterApi } from '@/api/master';
import { useToast } from '@/hooks/use-toast';
import { MasterOptions, Voice, VoicesResponse } from '@/types/master';

interface MasterState {
  data: MasterOptions | null;
  isLoading: boolean;
  error: string | null;
}

interface VoicesState {
  data: Voice[] | null;
  isLoading: boolean;
  error: string | null;
}

interface MasterContextType {
  options: MasterState;
  voices: VoicesState;
  fetchOptions: () => Promise<void>;
  fetchVoices: () => Promise<void>;
}

const MasterContext = createContext<MasterContextType | undefined>(undefined);

export function MasterProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<MasterState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [voices, setVoices] = useState<VoicesState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const { toast } = useToast();

  const fetchOptions = useCallback(async () => {
    setOptions(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await masterApi.getOptions();
      setOptions({
        data: response,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setOptions(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch master options',
      }));
      toast({
        description: 'Failed to fetch master options',
        variant: 'error',
      });
    }
  }, [toast]);

  const fetchVoices = useCallback(async () => {
    setVoices(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await masterApi.getVoices();
      // Response structure: { voices: Voice[] }
      setVoices({
        data: response.voices || [],
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setVoices(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch voices',
      }));
      toast({
        description: 'Failed to fetch voices',
        variant: 'error',
      });
    }
  }, [toast]);

  return (
    <MasterContext.Provider
      value={{
        options,
        voices,
        fetchOptions,
        fetchVoices,
      }}
    >
      {children}
    </MasterContext.Provider>
  );
}

export const useMaster = () => {
  const context = useContext(MasterContext);
  if (!context) {
    throw new Error('useMaster must be used within a MasterProvider');
  }
  return context;
};
